import React, { useState, useEffect, useRef } from 'react';
import { Download, Maximize2, Minimize2 } from 'lucide-react';
import { getPendientes, subirPendientes, eliminarPendiente, moverPendiente, moverTodosPendientes, urlVerPendiente, urlDescargarPendiente, getGestiones, descargarEnNavegador } from '../utils/api';
import PdfViewer from '../components/PdfViewer';

function formatBytes(b) {
  if (!b) return '—';
  if (b < 1048576) return (b / 1024).toFixed(0) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}
function formatDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('es-BO', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
}

export default function PendientesPage({ user, gestiones, onRefresh }) {
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(new Set()); // ids seleccionados para mover
  const [viewing, setViewing] = useState(null);
  const [pantallaCompleta, setPantallaCompleta] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [descripcion, setDescripcion] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [modalMover, setModalMover] = useState(null); // doc o 'todos'
  const [sesionDestino, setSesionDestino] = useState('');
  const fileRef = useRef();

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { setDocs(await getPendientes()); } catch {}
  };

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true); setUploadPct(0); setError('');
    try {
      await subirPendientes(Array.from(files), descripcion, setUploadPct);
      setDescripcion('');
      setOk('Documentos subidos a la bandeja de pendientes');
      setTimeout(() => setOk(''), 3000);
      await load();
      onRefresh?.();
    } catch (e) { setError(e.response?.data?.error || 'Error al subir'); }
    setUploading(false); setUploadPct(0);
  };

  const handleEliminar = async (doc) => {
    if (!window.confirm(`¿Eliminar "${doc.nombre_original}" de pendientes?`)) return;
    try {
      await eliminarPendiente(doc.id);
      if (viewing?.id === doc.id) setViewing(null);
      setSelected(s => { const n = new Set(s); n.delete(doc.id); return n; });
      await load();
      onRefresh?.();
    } catch (e) { setError(e.response?.data?.error || 'Error al eliminar'); }
  };

  const handleMover = async () => {
    if (!sesionDestino) return setError('Seleccione una sesión destino');
    setError('');
    try {
      if (modalMover === 'todos') {
        const ids = selected.size > 0 ? [...selected] : docs.map(d => d.id);
        await moverTodosPendientes(ids, sesionDestino);
        setOk(`${ids.length} documento(s) movido(s) a la sesión`);
      } else {
        await moverPendiente(modalMover.id, sesionDestino);
        setOk(`"${modalMover.nombre_original}" movido a la sesión`);
      }
      setTimeout(() => setOk(''), 3000);
      setModalMover(null); setSesionDestino(''); setSelected(new Set());
      await load();
      onRefresh?.();
    } catch (e) { setError(e.response?.data?.error || 'Error al mover'); }
  };

  const toggleSelect = (id) => {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const toggleAll = () => {
    if (selected.size === docs.length) setSelected(new Set());
    else setSelected(new Set(docs.map(d => d.id)));
  };

  const openPdf = (doc) => {
    setViewing(doc);
  };

  const handleDescargar = async (doc) => {
    const url = urlDescargarPendiente(doc.id);
    if (window.electronAPI) window.electronAPI.openExternal(url);
    else {
      try { await descargarEnNavegador(url, doc.nombre_original); }
      catch { window.open(url, '_blank'); }
    }
  };

  // Aplanar sesiones de todas las gestiones
  const todasSesiones = gestiones.flatMap(g =>
    (g.sesiones || []).map(s => ({
      ...s,
      label: `Gestión ${g.anio_inicio}-${g.anio_fin} — ${s.tipo === 'ordinaria' ? 'Sesión' : 'Ext.'} N°${String(s.numero).padStart(2,'0')}${s.fecha ? ` (${s.fecha})` : ''}`
    }))
  );

  return (
    <div className={`content-split ${viewing ? 'has-active' : ''}`}>
      {/* Panel izquierdo */}
      <div className="file-panel pendientes-panel">

        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Bandeja de pendientes</div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>Documentos sin sesión asignada</div>
            </div>
            <span style={{ background: '#fef3c7', color: '#854d0e', fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20 }}>
              {docs.length} doc{docs.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Alertas */}
          {error && <div className="alert alert-err" style={{ fontSize: 12, marginBottom: 8 }}>{error}<button onClick={() => setError('')} style={{ float:'right', background:'none', border:'none', cursor:'pointer' }}>✕</button></div>}
          {ok && <div className="alert alert-ok" style={{ fontSize: 12, marginBottom: 8 }}>{ok}</div>}

          {/* Subida — solo admin */}
          {user.rol === 'admin' && (
            <div>
              <input
                type="text"
                placeholder="Descripción opcional..."
                value={descripcion}
                onChange={e => setDescripcion(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 12, marginBottom: 6, outline: 'none' }}
              />
              <div
                style={{ border: '2px dashed #ddd', borderRadius: 8, padding: '10px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s' }}
                onClick={() => fileRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleUpload(Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf')); }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#c8102e'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#ddd'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c8102e" strokeWidth="1.5" style={{ marginBottom: 4 }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <p style={{ fontSize: 11, color: '#888', margin: 0 }}>
                  {uploading ? `Subiendo... ${uploadPct}%` : 'Arrastra PDFs aquí o haz clic'}
                </p>
              </div>
              {uploading && (
                <div style={{ height: 3, background: '#f0f0f0', borderRadius: 2, marginTop: 4 }}>
                  <div style={{ height: '100%', background: '#c8102e', width: `${uploadPct}%`, transition: 'width 0.2s' }}></div>
                </div>
              )}
              <input ref={fileRef} type="file" multiple accept=".pdf" style={{ display: 'none' }} onChange={e => handleUpload(e.target.files)} />
            </div>
          )}
        </div>

        {/* Acciones masivas */}
        {user.rol === 'admin' && docs.length > 0 && (
          <div style={{ padding: '8px 16px', borderBottom: '1px solid #f5f5f5', display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={selected.size === docs.length && docs.length > 0} onChange={toggleAll} style={{ cursor: 'pointer' }} />
            <span style={{ fontSize: 12, color: '#666', flex: 1 }}>
              {selected.size > 0 ? `${selected.size} seleccionado(s)` : 'Seleccionar todos'}
            </span>
            {(selected.size > 0 || docs.length > 0) && (
              <button
                className="btn-action btn-red btn-sm"
                onClick={() => { setModalMover('todos'); setSesionDestino(''); setError(''); }}
              >
                Mover {selected.size > 0 ? `${selected.size} selec.` : 'todos'} →
              </button>
            )}
          </div>
        )}

        {/* Lista */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {docs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 16px', color: '#aaa', fontSize: 12 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
              No hay documentos pendientes
            </div>
          )}
          {docs.map(doc => (
            <div
              key={doc.id}
              className={`file-card ${viewing?.id === doc.id ? 'active' : ''}`}
              style={{ borderLeft: selected.has(doc.id) ? '3px solid #c8102e' : undefined }}
              onClick={() => openPdf(doc)}
            >
              {user.rol === 'admin' && (
                <input type="checkbox" checked={selected.has(doc.id)} onChange={() => toggleSelect(doc.id)}
                  onClick={e => e.stopPropagation()} style={{ cursor: 'pointer', flexShrink: 0, marginTop: 2 }} />
              )}
              <div className="file-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
              </div>
              <div className="file-info">
                <div className="file-name" title={doc.nombre_original}>{doc.nombre_original}</div>
                {doc.descripcion && <div style={{ fontSize: 11, color: '#666', marginBottom: 2, fontStyle: 'italic' }}>{doc.descripcion}</div>}
                <div className="file-meta">{formatBytes(doc.tamano)} · {formatDate(doc.subido_en)}</div>
                <div className="file-actions">
                  <button className="file-btn" onClick={e => { e.stopPropagation(); openPdf(doc); }}>Ver</button>
                  <button className="file-btn" onClick={e => { e.stopPropagation(); handleDescargar(doc); }}>Descargar</button>
                  {user.rol === 'admin' && (
                    <>
                      <button className="file-btn" style={{ color: '#1d4ed8', borderColor: '#bfdbfe' }}
                        onClick={e => { e.stopPropagation(); setModalMover(doc); setSesionDestino(''); setError(''); }}>
                        Mover →
                      </button>
                      <button className="file-btn red" onClick={e => { e.stopPropagation(); handleEliminar(doc); }}>Eliminar</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Visor PDF */}
      <div className={`viewer-panel ${pantallaCompleta ? 'pantalla-completa' : ''}`}>
        {viewing ? (
          <>
            <div className="viewer-toolbar">
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{viewing.nombre_original}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="viewer-btn" title="Pantalla completa" onClick={() => setPantallaCompleta(v => !v)}>
                  {pantallaCompleta ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                </button>
                <button className="viewer-btn" title="Descargar" onClick={() => handleDescargar(viewing)}><Download size={15} /></button>
                <button className="viewer-btn" onClick={() => { setViewing(null); setPantallaCompleta(false); }}>✕</button>
              </div>
            </div>
            {window.electronAPI ? (
              <iframe
                key={viewing.id}
                src={urlVerPendiente(viewing.id)}
                style={{ flex: 1, width: '100%', border: 'none', display: 'block' }}
                title={viewing.nombre_original}
              />
            ) : (
              <PdfViewer key={viewing.id} src={urlVerPendiente(viewing.id)} />
            )}
          </>
        ) : (
          <div className="viewer-empty">
            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.2 }}>📋</div>
            <p>Selecciona un documento para visualizarlo</p>
          </div>
        )}
      </div>

      {/* Modal mover */}
      {modalMover && (
        <div className="modal-overlay" onClick={() => setModalMover(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMover === 'todos'
                  ? `Mover ${selected.size > 0 ? selected.size + ' documentos' : 'todos los pendientes'} a sesión`
                  : `Mover "${modalMover.nombre_original}"`}
              </h3>
              <button className="btn-icon" onClick={() => setModalMover(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-err">{error}</div>}
              <div className="form-group">
                <label>Seleccione la sesión destino</label>
                <select value={sesionDestino} onChange={e => setSesionDestino(e.target.value)} style={{ width: '100%' }}>
                  <option value="">— Seleccionar sesión —</option>
                  {todasSesiones.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
              <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                Los documentos se moverán permanentemente a la sesión seleccionada y desaparecerán de la bandeja de pendientes.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-action btn-outline" onClick={() => setModalMover(null)}>Cancelar</button>
              <button className="btn-action btn-red" onClick={handleMover} disabled={!sesionDestino}>
                Mover documentos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
